"use client";

import { Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts";

import { useCompetitorControllerGetArticleMonthlyStat } from "@/api/types/main";
import { LoadingSpinner } from "@/components/custom-ui/loading.spinner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { format } from "date-fns";
import { ru } from "date-fns/locale";

const chartConfig = {
  revenuePercent: {
    label: "Выручка Felfri, %",
    color: "hsl(var(--chart-1))",
  },
  orderCountPercent: {
    label: "Заказы Felfri, %",
    color: "hsl(var(--chart-2))",
  },
  competitorAvgPrice: {
    label: "Средняя цена конкурентов",
    color: "hsl(var(--chart-3))",
  },
  avgPrice: {
    label: "Средняя цена",
    color: "hsl(var(--chart-4))",
  },
} satisfies ChartConfig;

export function ChartBarMarketShareBySku({ sku }: { sku: number }) {
  const {
    data: chartData,
    isLoading,
    isError,
    error,
  } = useCompetitorControllerGetArticleMonthlyStat(Number(sku), {
    query: {
      enabled: !!sku,
    },
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <div>Ошибка при загрузке данных: {error?.message}</div>;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-none">
        <CardTitle className="text-sm 2xl:text-base">Доля выручки по SKU, %</CardTitle>
        <CardDescription className="text-xs 2xl:text-sm">
          Соотношение нашей доли в категориях и доли конкурентов
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 flex gap-4 justify-center">
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <ChartContainer config={chartConfig} className="aspect-[11/1] overflow-hidden">
            <ComposedChart accessibilityLayer data={chartData}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="month"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) => format(new Date(value), "MMM yyyy", { locale: ru })}
              />
              <YAxis yAxisId="percent" tickFormatter={(v) => `${v}%`} />
              <YAxis
                yAxisId="price"
                orientation="right"
                tickFormatter={(v) => v.toLocaleString("ru-RU")}
              />
              <ChartTooltip content={<ChartTooltipContent indicator="dashed" className="w-64" />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Bar
                yAxisId="percent"
                dataKey="revenuePercent"
                fill="var(--color-revenuePercent)"
                radius={4}
              />
              <Bar
                yAxisId="percent"
                dataKey="orderCountPercent"
                fill="var(--color-orderCountPercent)"
                radius={4}
              />
              <Line
                yAxisId="price"
                dataKey="competitorAvgPrice"
                stroke="var(--color-competitorAvgPrice)"
                type="monotone"
                dot={false}
                strokeDasharray="4 4"
              />
              <Line
                yAxisId="price"
                dataKey="avgPrice"
                stroke="var(--color-avgPrice)"
                type="monotone"
                dot={false}
              />
            </ComposedChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
